// src/App.js
import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';

// Import Firebase initialization
import './firebase/init';

// Import Providers for Auth and Permissions
import { AuthProvider } from './context/auth';
import { PermissionsProvider } from './context/permissions';
import { GoogleAdsProvider } from './context/ads';
import { Ga4Provider } from './context/ga4';
import { GmcProvider } from './context/gmc';
import { GtmProvider } from './context/gtm';
//import { CampaignManagerProvider } from './context/CampaignManagerContext';

// Import route protection components
import { ProtectedRoute, AdminRoute } from './components/ui/ProtectedRoutes';
import SimpleLayout from './components/ui/SimpleLayout';


// Eagerly loaded components (critical for initial render)
import Layout from './components/ui/Layout';
import LoginPage from './pages/core/login';
import Dashboard from './pages/core/dashboard';
import LoadingFallback from './components/ui/LoadingFallback';
import LandingPage from './pages/core/landing';

// Lazy loaded components (loaded on demand to improve initial load time).
const ProfilePage = lazy(() => import('./pages/core/profile'));
const FAQPage = lazy(() => import('./pages/core/faq'));
const DocumentationPage = lazy(() => import('./pages/documentation/documentation'));
const GA4Documentation = lazy(() => import('./pages/documentation/ga4Documentation'));
const GMCDocumentation = lazy(() => import('./pages/documentation/gmcDocumentation'));
const AdsDocumentation = lazy(() => import('./pages/documentation/adsDocumentation'));
const GTMDocumentation = lazy(() => import('./pages/documentation/gtmDocumentation'));
const AdminMenu = lazy(() => import('./pages/admin/menu'));
// const CM360AdminPanel = lazy(() => import('./pages/admin/CM360AdminPage'));
const AdsAdminPage = lazy(() => import('./pages/access-management/adsAccessManagement'));
const AnalyticsAdminPage = lazy(() => import('./pages/access-management/analyticsAccessManagement'));
const MerchantCentreAdminPage = lazy(() => import('./pages/access-management/gmcAccessManagement'));
const AdminPermissionsManager = lazy(() => import('./pages/admin/premissionsManager'));
const GoogleTagManagerAdminPage = lazy(() => import('./pages/access-management/gtmAccessManagement'));

// GMP Pages - lazy loaded
const AccessManagementMenu = lazy(() => import('./pages/access-management/menu'));
// const CM360AccessPage = lazy(() => import('./pages/gmp/CM360AccessPage'));
// const DV360AccessPage = lazy(() => import('./pages/gmp/DV360AccessPage'));
// const SA360AccessPage = lazy(() => import('./pages/gmp/SA360AccessPage'));
// const GA4AccessPage = lazy(() => import('./pages/gmp/GA4AccessPage'));
// const MerchantCentreAccessPage = lazy(() => import('./pages/gmp/MerchantCentreAccessPage'));
// const GoogleTagManagerAccessPage = lazy(() => import('./pages/gmp/GoogleTagManagerAccessPage'));

// Meta Pages - lazy loaded
const MetaMenu = lazy(() => import('./pages/access-requests/meta-menu'));
const BusinessManagerAccessPage = lazy(() => import('./pages/access-requests/metaAccessRequests'));

// GCP Pages - lazy loaded
const GCPMenu = lazy(() => import('./pages/access-requests/gcp-menu'));
const GCPProjectAccessPage = lazy(() => import('./pages/access-requests/gcpAccessRequests'));

// Request Access Pages - lazy loaded
const RequestAccessMenu = lazy(() => import('./pages/access-requests/menu'));
const GoogleAdsRequestPage = lazy(() => import('./pages/access-requests/adsAccessRequests'));
const AnalyticsRequestPage = lazy(() => import('./pages/access-requests/analyticsAccessRequests'));
const MerchantCentreRequestPage = lazy(() => import('./pages/access-requests/gmcAccessRequests'));
const GoogleTagManagerRequestPage = lazy(() => import('./pages/access-requests/gtmAccessRequests'));
// Simple loading component for lazy-loaded routes
const SuspenseFallback = () => (
	<div className="flex items-center justify-center py-12">
		<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
	</div>
);

const AppWithRoutes = () => {
	return (
		<Routes>
			{/* Landing page route */}

			{/* Standalone login route */}
			<Route path="/" element={<LoginPage />} />

			{/* Protected routes */}
			{/* This includes the dashboard, request access, access management, and permissions routes */}
			<Route path="/dashboard" element={
				<ProtectedRoute>
					<Layout />
				</ProtectedRoute>
			}>
				{/* Main Dashboard index route */}
				<Route index element={<Dashboard />} />

				{/* Profile Route - accessible to authenticated users*/}
				<Route path="profile" element={
					<Suspense fallback={<SuspenseFallback />}>
						<ProfilePage />
					</Suspense>
				} />

				{/* Request Access Routes - accessible to all authenticated users */}
				<Route path="request-access">
					<Route index element={
						<Suspense fallback={<SuspenseFallback />}>
							<RequestAccessMenu />
						</Suspense>
					} />
					<Route path="google-ads" element={
						<Suspense fallback={<SuspenseFallback />}>
							<GoogleAdsRequestPage />
						</Suspense>
					} />
					<Route path="analytics" element={
						<Suspense fallback={<SuspenseFallback />}>
							<AnalyticsRequestPage />
						</Suspense>
					} />
					<Route path="gtm" element={
						<Suspense fallback={<SuspenseFallback />}>
							<GoogleTagManagerRequestPage />
						</Suspense>
					} />
					<Route path="gmc" element={
						<Suspense fallback={<SuspenseFallback />}>
							<MerchantCentreRequestPage />
						</Suspense>
					} />
				</Route>

				{/* Access Management Routes - accessible to authenticated users with the Ads Admin permission*/}
				<Route path="access-management">
					<Route index element={
						<Suspense fallback={<SuspenseFallback />}>
							<AccessManagementMenu />
						</Suspense>
					} />
					<Route path="google-ads" element={
						<AdminRoute route="admin/ads">
							<Suspense fallback={<SuspenseFallback />}>
								<AdsAdminPage />
							</Suspense>
						</AdminRoute>
					} />
					<Route path="analytics" element={
						<AdminRoute route="admin/ga4">
							<Suspense fallback={<SuspenseFallback />}>
								<AnalyticsAdminPage />
							</Suspense>
						</AdminRoute>
					} />
					<Route path="gtm" element={
						<AdminRoute route="admin/gtm">
							<Suspense fallback={<SuspenseFallback />}>
								<GoogleTagManagerAdminPage />
							</Suspense>
						</AdminRoute>
					} />
					<Route path="gmc" element={
						<AdminRoute route="admin/gmc">
							<Suspense fallback={<SuspenseFallback />}>
								<MerchantCentreAdminPage />
							</Suspense>
						</AdminRoute>
					} />
				</Route>

				{/* Admin Panel Routes - Protected by AdminRoute with specific permissions */}
				<Route path="admin">
					<Route index element={
						<AdminRoute route="admin/menu">
							<Suspense fallback={<SuspenseFallback />}>
								<AdminMenu />
							</Suspense>
						</AdminRoute>
					} />

					{/* Add this new route for the permissions manager */}
					<Route path="permissions" element={
						<AdminRoute route="admin/settings">
							<Suspense fallback={<SuspenseFallback />}>
								<AdminPermissionsManager />
							</Suspense>
						</AdminRoute>
					} />
				</Route>

				{/* Meta Routes */}
				{/* <Route path="meta">
					<Route index element={
						<Suspense fallback={<SuspenseFallback />}>
							<MetaMenu />
						</Suspense>
					} />
					<Route path="business-manager" element={
						<Suspense fallback={<SuspenseFallback />}>
							<BusinessManagerAccessPage />
						</Suspense>
					} />
				</Route> */}

				{/* GCP Routes */}
				{/* <Route path="gcp">
					<Route index element={
						<Suspense fallback={<SuspenseFallback />}>
							<GCPMenu />
						</Suspense>
					} />
					<Route path="projects" element={
						<Suspense fallback={<SuspenseFallback />}>
							<GCPProjectAccessPage />
						</Suspense>
					} />
				</Route> */}

				{/* Protected FAQ Route */}
				{/* <Route path="faq" element={
					<Suspense fallback={<SuspenseFallback />}>
						<FAQPage />
					</Suspense>
				} /> */}

				{/* Documentation Routes - accessible to all authenticated users */}
				<Route path="documentation" element={
					<Suspense fallback={<SuspenseFallback />}>
						<DocumentationPage />
					</Suspense>
				} />
				<Route path="documentation/ga4" element={
					<Suspense fallback={<SuspenseFallback />}>
						<GA4Documentation />
					</Suspense>
				} />
				<Route path="documentation/gmc" element={
					<Suspense fallback={<SuspenseFallback />}>
						<GMCDocumentation />
					</Suspense>
				} />
				<Route path="documentation/ads" element={
					<Suspense fallback={<SuspenseFallback />}>
						<AdsDocumentation />
					</Suspense>
				} />
				<Route path="documentation/gtm" element={
					<Suspense fallback={<SuspenseFallback />}>
						<GTMDocumentation />
					</Suspense>
				} />
			</Route>
		</Routes>
	);
};

const App = () => {
	// Provide necessary contexts to the application
	return (
		<Router>
			<GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
				<AuthProvider>
					<PermissionsProvider>
						<GoogleAdsProvider>
							<Ga4Provider>
								<GmcProvider>
									<GtmProvider>
								{/* <CampaignManagerProvider> */}
								<AppWithRoutes />
								{/* </CampaignManagerProvider> */}
									</GtmProvider>
								</GmcProvider>
							</Ga4Provider>
						</GoogleAdsProvider>
					</PermissionsProvider>
				</AuthProvider>
			</GoogleOAuthProvider>
		</Router>
	);
};

export default App;
